import cn from 'classnames'
import { memo, useCallback, useMemo, useRef } from 'react'

import { IContact } from 'clickNgo/entities/contact'
import { InfoBlock, InfoBlockRounded, InfoBlockType } from 'cng-common/components/infoBlock'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { SimpleTable, usePagination } from 'cng-common/components/simpleTable'
import { Translation } from 'cng-common/features/translations'

import { contactEditorTranslation } from '../../../../../translation'
import { TDuplicateHPRole } from '../../models'

import { contactTableColumns } from './configs'

import styles from './PartnerHPRoles.module.scss'

type TProps = {
  duplicateHPRoles: TDuplicateHPRole[]
}

export const PartnerHPRoles = memo<TProps>(({ duplicateHPRoles }) => {
  const { current: pageSize } = useRef(5)
  const contactsWithDuplicates = useMemo(
    () =>
      duplicateHPRoles.reduce<IContact[]>((acc, duplicate) => [...acc, ...duplicate.contacts], []),
    [duplicateHPRoles]
  )
  const { onPageChange, page, paginatedList, total } = usePagination(
    contactsWithDuplicates,
    pageSize
  )

  const hasDuplicateHPRole = Boolean(duplicateHPRoles.length)

  const hpRolesDuplicateWarning = useMemo(
    () => (
      <Translation
        text={contactEditorTranslation.warnings.hasHPRolesDuplicates}
        replaceParams={[
          { key: '{roles}', value: duplicateHPRoles.map(({ role }) => role).join(', ') },
        ]}
        span={true}
      />
    ),
    [duplicateHPRoles]
  )

  const renderContactHeader = useCallback(
    () => (
      <SimpleTable.HeaderRow>
        {...contactTableColumns.map((column) => (
          <SimpleTable.HeaderCell key={column.columnField as string} style={column.styles}>
            <Translation text={column.columnTitle} span />
          </SimpleTable.HeaderCell>
        ))}
      </SimpleTable.HeaderRow>
    ),
    []
  )

  const renderContactBody = useCallback(
    () =>
      paginatedList.map((contact, index) => (
        <SimpleTable.BodyRow
          key={contact.contactId}
          className={cn({
            [styles.filled]: index % 2,
            [styles.duplicate]: duplicateHPRoles?.some((el) => contact.hpRoles.includes(el.role)),
          })}
        >
          {...contactTableColumns.map((column) => {
            const field = column.columnField
            const value = contact[field] as string[] & string

            if (column.render) {
              return (
                <SimpleTable.BodyCell key={column.columnField as string}>
                  {column.render(contact)}
                </SimpleTable.BodyCell>
              )
            }

            return (
              <SimpleTable.BodyCell key={column.columnField as string}>
                {<>{value || '-'}</>}
              </SimpleTable.BodyCell>
            )
          })}
        </SimpleTable.BodyRow>
      )),
    [duplicateHPRoles, paginatedList]
  )

  return (
    <div className={styles.container}>
      <RenderWhen condition={hasDuplicateHPRole}>
        <InfoBlock
          text={hpRolesDuplicateWarning}
          type={InfoBlockType.Danger}
          rounded={InfoBlockRounded.Medium}
        />
      </RenderWhen>
      <div className={styles.tableWrapper}>
        <SimpleTable
          className={cn(styles.table, styles.compact, styles.partnerHPRolesTable)}
          placeholderColspan={3}
        >
          {renderContactHeader()}
          {renderContactBody()}
        </SimpleTable>
      </div>
      <RenderWhen condition={total > pageSize}>
        <div className={styles.tableFooter}>
          <SimpleTable.Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            classes={{ container: styles.pagination, button: styles.paginationButton }}
            onPageChange={onPageChange}
          />
        </div>
      </RenderWhen>
    </div>
  )
})

PartnerHPRoles.displayName = 'PartnerHPRoles'
